import type { ExposeImage, ExposeMakler } from '../api/types';

export type ExposeLayout = 'STANDARD' | 'PFAU_1';
export type ExposeBrandMode = 'MAKLERPASS' | 'PARTNER' | 'NEUTRAL';

export const EXPOSE_LAYOUTS: { key: ExposeLayout; label: string }[] = [
  { key: 'STANDARD', label: 'Standard (Der Pass)' },
  { key: 'PFAU_1', label: 'Layout 1 Pfau (Editorial Magazin)' },
];

export const EXPOSE_BRAND_MODES: { key: ExposeBrandMode; label: string }[] = [
  { key: 'MAKLERPASS', label: 'Maklerpass (Dachmarke)' },
  { key: 'PARTNER', label: 'Partner-Marke (eigene Firma, z. B. BETA)' },
  { key: 'NEUTRAL', label: 'Neutral (nur Objekt & Ansprechpartner)' },
];

// Angaben eines Exposés (packages/expose/expose.schema.json); der Partner (Kontaktseite) kommt aus seinem Profil.
export interface ExposeData {
  schema_version?: string;
  meta?: { objektnummer?: string; layout?: ExposeLayout; brand_mode?: ExposeBrandMode };
  objekt?: {
    titel?: string;
    untertitel?: string;
    kurzbeschreibung?: string;
    highlights?: string[];
    vermarktungsart?: 'KAUF' | 'MIETE';
    objektart?: string;
    objekttyp?: string;
    adresse?: { strasse?: string; hausnummer?: string; plz?: string; ort?: string; ortsteil?: string; adresse_veroeffentlichen?: boolean };
  };
  preise?: {
    kaufpreis?: number | null;
    preis_auf_anfrage?: boolean;
    kaufpreis_pro_m2?: number | null;
    kaltmiete?: number | null;
    nebenkosten_monatlich?: number | null;
    heizkosten_monatlich?: number | null;
    warmmiete?: number | null;
    kaution?: number | string | null;
    hausgeld_monatlich?: number | null;
    davon_erhaltungsruecklage?: number | null;
    grundsteuer_jaehrlich?: number | null;
    stellplatz_kaufpreis?: number | null;
    stellplatz_miete?: number | null;
    provision?: { provisionsfrei?: boolean; kaeufer_prozent_inkl_ust?: number | null; text?: string };
  };
  flaechen?: {
    wohnflaeche_m2?: number | null;
    nutzflaeche_m2?: number | null;
    grundstuecksflaeche_m2?: number | null;
    zimmer?: number | null;
    schlafzimmer?: number | null;
    badezimmer?: number | null;
    gaeste_wc?: boolean;
    etage?: number | null;
    etagen_gesamt?: number | null;
    balkon_terrasse_m2?: number | null;
    garten_m2?: number | null;
    keller_m2?: number | null;
  };
  bau_und_zustand?: {
    baujahr?: number | null;
    zustand?: string | null;
    modernisierungen?: { jahr: number; beschreibung: string }[];
    ausstattungsqualitaet?: string | null;
    bauweise?: string;
    denkmalschutz?: boolean;
    vermietet?: boolean | null;
    bezugsfrei_ab?: string;
  };
  energie?: {
    energieausweis?: string;
    ausweisart?: string | null;
    gueltig_bis?: string | null;
    endenergie_kwh_m2a?: number | null;
    energieeffizienzklasse?: string | null;
    wesentlicher_energietraeger?: string;
    heizungsart?: string;
    baujahr_gebaeude?: number | null;
    baujahr_heizung?: number | null;
    pflichtangaben_text?: string;
  };
  ausstattung?: { merkmale?: string[]; stellplaetze?: { art: string; anzahl?: number; kaufpreis?: number | null }[]; bodenbelaege?: string[] };
  beschreibungen?: { objekt?: string; ausstattung?: string; lage?: string; sonstiges?: string };
  lage?: { entfernungen?: { kategorie: string; name?: string; minuten: number; verkehrsmittel?: string; km?: number | null }[] };
  kontakt?: { besichtigung_text?: string };
  rechtliches?: {
    provisionshinweis?: string;
    angaben_ohne_gewaehr?: string;
    datenschutz_url?: string;
    impressum?: {
      firma?: string;
      zusatz?: string;
      vertreten_durch?: string;
      anschrift?: string;
      register?: string;
      ust_id?: string;
      standort?: string;
      erlaubnis_34c?: string;
      aufsichtsbehoerde?: string;
      kontakt?: string;
    };
  };
}

export const LABELS: Record<string, string> = {
  EINBAUKUECHE: 'Einbauküche',
  BALKON: 'Balkon',
  TERRASSE: 'Terrasse',
  GARTEN: 'Garten',
  AUFZUG: 'Aufzug',
  KELLER: 'Keller',
  GAESTE_WC: 'Gäste-WC',
  TIEFGARAGE: 'Tiefgarage',
  GARAGE: 'Garage',
  STELLPLATZ: 'Stellplatz',
  CARPORT: 'Carport',
  AUSSEN: 'Außenstellplatz',
  DUPLEX: 'Duplex-Stellplatz',
  AUSSENSTELLPLATZ: 'Außenstellplatz',
  GLASFASER: 'Glasfaser',
  BARRIEREFREI: 'Barrierefrei',
  KAMIN: 'Kamin',
  SAUNA: 'Sauna',
  FUSSBODENHEIZUNG: 'Fußbodenheizung',
  KLIMAANLAGE: 'Klimaanlage',
  SOLARANLAGE: 'Photovoltaik',
  PHOTOVOLTAIK: 'Photovoltaik',
  WAERMEPUMPE: 'Wärmepumpe',
  DACHBODEN: 'Dachboden',
  WINTERGARTEN: 'Wintergarten',
  POOL: 'Pool',
  ALARMANLAGE: 'Alarmanlage',
  SMART_HOME: 'Smart Home',
  EINLIEGERWOHNUNG: 'Einliegerwohnung',
  ABSTELLRAUM: 'Abstellraum',
  WASCHKUECHE: 'Waschküche',
  E_LADESTATION: 'E-Ladestation',
  DENKMAL_OPTIK: 'Stuck',
  MOEBLIERT: 'Möbliert',
  GEPFLEGT: 'Gepflegt',
  ERSTBEZUG: 'Erstbezug',
  NEUWERTIG: 'Neuwertig',
  MODERNISIERT: 'Modernisiert',
  SANIERT: 'Saniert',
  TEILSANIERT: 'Teilsaniert',
  RENOVIERT: 'Renoviert',
  RENOVIERUNGSBEDUERFTIG: 'Renovierungsbedürftig',
  SANIERUNGSBEDUERFTIG: 'Sanierungsbedürftig',
  ABBRUCHREIF: 'Abbruchreif',
  NORMAL: 'Normal',
  GEHOBEN: 'Gehoben',
  LUXUS: 'Luxus',
  EINFACH: 'Einfach',
  WOHNUNG: 'Wohnung',
  HAUS: 'Haus',
  MEHRFAMILIENHAUS: 'Mehrfamilienhaus',
  GRUNDSTUECK: 'Grundstück',
  GEWERBE: 'Gewerbe',
  ANLAGE: 'Kapitalanlage',
  BAHNHOF: 'Bahnhof',
  UBAHN: 'U-Bahn',
  BUS: 'Bus',
  OEPNV: 'ÖPNV',
  SCHULE: 'Schule',
  KITA: 'Kita',
  EINKAUF: 'Einkauf',
  AUTOBAHN: 'Autobahn',
  ZENTRUM: 'Zentrum',
  ARZT: 'Arzt',
  FLUGHAFEN: 'Flughafen',
  PARK: 'Park',
  FUSS: 'zu Fuß',
  AUTO: 'Auto',
  RAD: 'Fahrrad',
};

// Auswahllisten für den Editor (Reihenfolge = Anzeige).
export const MERKMALE = [
  'EINBAUKUECHE',
  'BALKON',
  'TERRASSE',
  'GARTEN',
  'AUFZUG',
  'KELLER',
  'GAESTE_WC',
  'BARRIEREFREI',
  'FUSSBODENHEIZUNG',
  'KAMIN',
  'KLIMAANLAGE',
  'SAUNA',
  'WINTERGARTEN',
  'POOL',
  'SOLARANLAGE',
  'WAERMEPUMPE',
  'SMART_HOME',
  'ALARMANLAGE',
  'GLASFASER',
  'E_LADESTATION',
  'DACHBODEN',
  'ABSTELLRAUM',
  'WASCHKUECHE',
  'EINLIEGERWOHNUNG',
  'DENKMAL_OPTIK',
  'MOEBLIERT',
  'GARAGE',
  'TIEFGARAGE',
  'CARPORT',
  'STELLPLATZ',
];
export const ZUSTAENDE = [
  'ERSTBEZUG',
  'NEUWERTIG',
  'GEPFLEGT',
  'MODERNISIERT',
  'RENOVIERT',
  'TEILSANIERT',
  'SANIERT',
  'RENOVIERUNGSBEDUERFTIG',
  'SANIERUNGSBEDUERFTIG',
  'ABBRUCHREIF',
];
export const QUALITAETEN = ['EINFACH', 'NORMAL', 'GEHOBEN', 'LUXUS'];
export const OBJEKTARTEN = ['WOHNUNG', 'HAUS', 'MEHRFAMILIENHAUS', 'GRUNDSTUECK', 'GEWERBE', 'ANLAGE'];
export const ENTFERNUNG_KATEGORIEN = ['BAHNHOF', 'UBAHN', 'BUS', 'SCHULE', 'KITA', 'EINKAUF', 'AUTOBAHN', 'ZENTRUM', 'ARZT', 'FLUGHAFEN', 'PARK'];
export const VERKEHRSMITTEL = ['FUSS', 'RAD', 'AUTO', 'OEPNV'];
export const STELLPLATZ_ARTEN = ['GARAGE', 'TIEFGARAGE', 'CARPORT', 'AUSSEN', 'DUPLEX'];
export const ENERGIEAUSWEIS = {
  VORHANDEN: 'liegt vor',
  BEI_BESICHTIGUNG: 'liegt zur Besichtigung vor',
  IN_ERSTELLUNG: 'in Erstellung',
  NICHT_ERFORDERLICH: 'nicht erforderlich',
};
export const ENERGIE_KLASSEN = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const HUES = [150, 140, 128, 112, 92, 72, 52, 35, 22];
export const ACCENTS = ['#C8A24E', '#7D5F22', '#142238', '#DC2626', '#1E2430', '#A31621'];
export const STATUS_LABELS: Record<string, string> = {
  ENTWURF: 'Entwurf',
  ZUR_FREIGABE: 'Zur Makler-Freigabe',
  FREIGEGEBEN: 'Vom Makler freigegeben',
  VEROEFFENTLICHT: 'Veröffentlicht (Inserat aktiv)',
  RESERVIERT: 'Reserviert',
  VERKAUFT: 'Verkauft',
};

const NB = '\u00A0';
export const lab = (s?: string | null): string | null =>
  s == null || s === ''
    ? null
    : (LABELS[s] ?? (s.charAt(0) + s.slice(1).toLowerCase()).replace(/_/g, ' ').replace(/ae/g, 'ä').replace(/oe/g, 'ö').replace(/ue/g, 'ü'));
const num = (n: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(n);
const eur = (n?: number | null) => (n == null ? null : `${num(n)}${NB}€`);
const m2 = (n?: number | null) => (n == null ? null : `${num(n)}${NB}m²`);
const has = (v: unknown): boolean => v != null && v !== '' && v !== false;
const datum = (s?: string | null) => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

export interface TextBlock {
  li: boolean;
  text: string;
}
export const blocks = (t?: string): TextBlock[] =>
  (t ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (/^[-•*]\s+/.test(l) ? { li: true, text: l.replace(/^[-•*]\s+/, '') } : { li: false, text: l }));

export interface Slot {
  id: string;
  src: string;
  titel: string;
  fit?: 'cover' | 'contain';
  span?: boolean;
  // visualisierung: virtuell eingerichtet (KI) – im Exposé gekennzeichnet, sonst irreführend.
  visualisierung?: boolean;
}
export interface KV {
  k: string;
  v: string;
}

// pflichtangaben erzeugt die Zeile nach § 87 GEG aus den Energieangaben.
export function pflichtangaben(e: ExposeData['energie'] = {}, baujahr?: number | null): string {
  if (e.energieausweis !== 'VORHANDEN') return '';
  const bedarf = e.ausweisart === 'BEDARF';
  return [
    bedarf ? 'Energiebedarfsausweis' : 'Energieverbrauchsausweis',
    e.endenergie_kwh_m2a != null ? `${bedarf ? 'Endenergiebedarf' : 'Endenergieverbrauch'} ${num(e.endenergie_kwh_m2a)} kWh/(m²·a)` : null,
    e.wesentlicher_energietraeger || null,
    (e.baujahr_gebaeude ?? baujahr) ? `Baujahr ${e.baujahr_gebaeude ?? baujahr}` : null,
    e.energieeffizienzklasse ? `Effizienzklasse ${e.energieeffizienzklasse}` : null,
  ]
    .filter(Boolean)
    .join(', ');
}

// buildExposeView übersetzt die Angaben in die Inhalte der Seiten (Logik des Exposé-Konfigurators).
export function buildExposeView(
  d: ExposeData,
  opts: { objectNumber: string; status: string; makler: ExposeMakler | null; images: ExposeImage[]; whatsapp: boolean },
) {
  const o = d.objekt ?? {},
    p = d.preise ?? {},
    f = d.flaechen ?? {},
    b = d.bau_und_zustand ?? {},
    e = d.energie ?? {};
  const aus = d.ausstattung ?? {},
    bes = d.beschreibungen ?? {},
    k = opts.makler,
    r = d.rechtliches ?? {};
  const objnr = opts.objectNumber || d.meta?.objektnummer || '';
  const miete = o.vermarktungsart === 'MIETE';
  const a = o.adresse ?? {};
  const ortsteil = a.ortsteil ? (a.ort ? ` · ${a.ortsteil}` : a.ortsteil) : '';
  const ortZeile =
    (a.adresse_veroeffentlichen && a.strasse ? `${`${a.strasse} ${a.hausnummer ?? ''}`.trim()} · ` : '') + [a.plz, a.ort].filter(Boolean).join(NB) + ortsteil;
  const titel = o.titel ?? '';
  const titelSize = titel.length > 70 ? 38 : titel.length > 45 ? 44 : 52;

  const preisAnfrage = !!p.preis_auf_anfrage;
  const preisLabel = miete ? 'Kaltmiete' : 'Kaufpreis';
  const preisWert = preisAnfrage ? 'auf Anfrage' : ((miete ? eur(p.kaltmiete) : eur(p.kaufpreis)) ?? 'auf Anfrage');
  const facts: KV[] = [{ k: preisLabel, v: preisWert }];
  if (f.wohnflaeche_m2) facts.push({ k: 'Wohnfläche', v: m2(f.wohnflaeche_m2)! });
  if (f.zimmer) facts.push({ k: 'Zimmer', v: num(f.zimmer) });
  if (facts.length < 3 && f.grundstuecksflaeche_m2) facts.push({ k: 'Grundstück', v: m2(f.grundstuecksflaeche_m2)! });
  if (facts.length < 3 && f.nutzflaeche_m2) facts.push({ k: 'Nutzfläche', v: m2(f.nutzflaeche_m2)! });

  const istHaus = o.objektart === 'HAUS';
  const bad = f.badezimmer != null ? num(f.badezimmer) + (f.gaeste_wc ? ' + Gäste-WC' : '') : f.gaeste_wc ? 'Gäste-WC' : null;
  const etage = istHaus
    ? f.etagen_gesamt != null
      ? num(f.etagen_gesamt)
      : null
    : f.etage != null
      ? (f.etage === 0 ? 'Erdgeschoss' : `${f.etage}. OG`) + (f.etagen_gesamt ? ` von ${f.etagen_gesamt}` : '')
      : null;
  const objektdaten = (
    [
      ['Objekttyp', o.objekttyp || lab(o.objektart)],
      ['Wohnfläche', m2(f.wohnflaeche_m2)],
      ['Nutzfläche', m2(f.nutzflaeche_m2)],
      ['Grundstück', m2(f.grundstuecksflaeche_m2)],
      ['Zimmer', f.zimmer != null ? num(f.zimmer) : null],
      ['Schlafzimmer', f.schlafzimmer != null ? num(f.schlafzimmer) : null],
      ['Badezimmer', bad],
      [istHaus ? 'Geschosse' : 'Etage', etage],
      ['Balkon / Terrasse', m2(f.balkon_terrasse_m2)],
      ['Garten', m2(f.garten_m2)],
      ['Keller', m2(f.keller_m2)],
      ['Baujahr', b.baujahr != null ? String(b.baujahr) : null],
      ['Zustand', lab(b.zustand)],
      ['Bauweise', b.bauweise],
      ['Ausstattung', lab(b.ausstattungsqualitaet)],
      ['Denkmalschutz', b.denkmalschutz ? 'Ja' : null],
      ['Vermietet', miete || b.vermietet == null ? null : b.vermietet ? 'Ja' : 'Nein'],
      [miete ? 'Verfügbar ab' : 'Bezugsfrei ab', b.bezugsfrei_ab],
    ] as [string, string | null | undefined][]
  )
    .filter(([, v]) => has(v))
    .map(([key, v]) => ({ k: key, v: String(v) }));

  const pm2 = p.kaufpreis_pro_m2 || (p.kaufpreis && f.wohnflaeche_m2 ? Math.round(p.kaufpreis / f.wohnflaeche_m2) : null);
  const stp = (aus.stellplaetze ?? [])[0];
  const stpLabel = stp ? `${lab(stp.art)}${(stp.anzahl ?? 1) > 1 ? ` (${stp.anzahl}×)` : ''}` : 'Stellplatz';
  const prov = p.provision ?? {};
  const provKurz = prov.provisionsfrei
    ? 'provisionsfrei'
    : prov.kaeufer_prozent_inkl_ust != null
      ? `${num(prov.kaeufer_prozent_inkl_ust)}${NB}%${NB}inkl.${NB}MwSt.`
      : null;
  const kaution = typeof p.kaution === 'number' ? eur(p.kaution) : p.kaution || null;
  const warm =
    p.warmmiete ?? (p.kaltmiete != null && p.nebenkosten_monatlich != null ? p.kaltmiete + p.nebenkosten_monatlich + (p.heizkosten_monatlich ?? 0) : null);
  const preise = (
    (miete
      ? [
          ['Nebenkosten monatlich', eur(p.nebenkosten_monatlich)],
          ['Heizkosten monatlich', eur(p.heizkosten_monatlich)],
          ['Warmmiete', eur(warm)],
          ['Kaution', kaution],
          [`${stpLabel} / Monat`, eur(p.stellplatz_miete)],
          ['Provision', prov.provisionsfrei ? 'provisionsfrei' : null],
        ]
      : [
          ['Preis pro m²', preisAnfrage ? null : eur(pm2)],
          [stpLabel, eur(p.stellplatz_kaufpreis ?? stp?.kaufpreis)],
          ['Hausgeld monatlich', eur(p.hausgeld_monatlich)],
          ['davon Erhaltungsrücklage', eur(p.davon_erhaltungsruecklage)],
          ['Grundsteuer jährlich', eur(p.grundsteuer_jaehrlich)],
          ['Käuferprovision', provKurz],
        ]) as [string, string | null][]
  )
    .filter(([, v]) => has(v))
    .map(([key, v]) => ({ k: key, v: v! }));
  const provisionTitel = miete ? 'Provision' : 'Käuferprovision';
  const provisionText = prov.provisionsfrei
    ? miete
      ? 'Für den Mieter provisionsfrei.'
      : 'Für den Käufer provisionsfrei.'
    : prov.text || r.provisionshinweis || '';

  // Bilder: Titelbild, erstes weiteres Foto, Galerie (je 4), Grundrisse, Lagekarte.
  const bilder = [...opts.images].sort((x, y) => x.sort_order - y.sort_order);
  const slot = (x: ExposeImage): Slot => ({ id: x.id, src: x.url, titel: x.title, visualisierung: x.ai_edit === 'STAGING' });
  const titelBild = bilder.find((x) => x.is_cover) ?? bilder.find((x) => x.category !== 'GRUNDRISS' && x.category !== 'LAGE');
  const plaene = bilder.filter((x) => x.category === 'GRUNDRISS');
  const karte = bilder.find((x) => x.category === 'LAGE');
  const fotos = bilder.filter((x) => x !== titelBild && x.category !== 'GRUNDRISS' && x.category !== 'LAGE');
  const cover: Slot = titelBild ? { ...slot(titelBild), titel: `Titelbild · ${titelBild.title}` } : { id: 'titel', src: '', titel: 'Titelbild' };
  const img2 = fotos[0] ? slot(fotos[0]) : null;
  const rest: Slot[] = [
    ...fotos.slice(1).map((x): Slot => ({ ...slot(x), fit: 'cover' })),
    ...plaene.slice(1).map((x): Slot => ({ ...slot(x), fit: 'contain' })),
  ];
  const layout: ExposeLayout = d.meta?.layout === 'PFAU_1' ? 'PFAU_1' : 'STANDARD';
  const brandMode: ExposeBrandMode =
    d.meta?.brand_mode === 'PARTNER' || d.meta?.brand_mode === 'NEUTRAL' ? d.meta.brand_mode : 'MAKLERPASS';
  const chunkSize = layout === 'PFAU_1' ? 3 : 4;
  const chunks: Slot[][] = [];
  for (let i = 0; i < rest.length; i += chunkSize) chunks.push(rest.slice(i, i + chunkSize));
  let n = 3;
  const galerie = chunks.map((bs) => ({
    nr: ++n,
    // 1 oder 2 Bilder: volle Breite (quer), 3 Bilder: erstes breit, 4 Bilder: Raster.
    bilder: bs.map((x, j) => ({ ...x, span: (bs.length === 3 && j === 0) || bs.length <= 2 })),
  }));
  const hasGrundriss = plaene.length > 0;
  const pn = { blick: 2, objekt: 3, plan: hasGrundriss ? ++n : null, lage: ++n, kontakt: ++n };

  const ev = e.energieausweis === 'VORHANDEN';
  const kl = e.energieeffizienzklasse ?? null;
  const energie = {
    has: ev && !!kl,
    kopf: ev
      ? [e.ausweisart === 'BEDARF' ? 'Bedarfsausweis' : 'Verbrauchsausweis', e.gueltig_bis ? `gültig bis ${datum(e.gueltig_bis)}` : null]
          .filter(Boolean)
          .join(' · ')
      : '',
    skala: ENERGIE_KLASSEN.map((c, i) => ({ k: c, color: `oklch(0.74 0.14 ${HUES[i]})`, on: c === kl })),
    werte: (
      [
        [
          e.ausweisart === 'BEDARF' ? 'Endenergiebedarf' : 'Endenergieverbrauch',
          e.endenergie_kwh_m2a != null ? `${num(e.endenergie_kwh_m2a)}${NB}kWh/(m²·a)` : null,
        ],
        ['Effizienzklasse', kl],
        ['Energieträger', e.wesentlicher_energietraeger],
        ['Heizung', e.heizungsart ? e.heizungsart + (e.baujahr_heizung ? ` (${e.baujahr_heizung})` : '') : null],
      ] as [string, string | null | undefined][]
    )
      .filter(([, v]) => has(v))
      .map(([key, v]) => ({ k: key, v: v! })),
    pflicht: ev
      ? e.pflichtangaben_text || pflichtangaben(e, b.baujahr)
      : ((
          {
            BEI_BESICHTIGUNG: 'Der Energieausweis liegt zur Besichtigung vor.',
            IN_ERSTELLUNG: 'Der Energieausweis befindet sich in Erstellung.',
            NICHT_ERFORDERLICH: 'Für dieses Objekt ist kein Energieausweis erforderlich.',
          } as Record<string, string>
        )[e.energieausweis ?? ''] ?? ''),
  };

  const merkmale = (aus.merkmale ?? []).map((m) => lab(m)!).filter(Boolean);
  const sideInfo = (
    [
      ['Bodenbeläge', (aus.bodenbelaege ?? []).join(', ')],
      ['Stellplatz', (aus.stellplaetze ?? []).map((s) => `${s.anzahl ?? 1}${NB}× ${lab(s.art)}${s.kaufpreis ? ` · ${eur(s.kaufpreis)}` : ''}`).join(', ')],
    ] as [string, string][]
  )
    .filter(([, v]) => has(v))
    .map(([key, v]) => ({ k: key, v }));

  const kapitel = hasGrundriss ? 4 : 3;
  const entf = (d.lage?.entfernungen ?? []).slice(0, 4).map((x) => ({
    kat: lab(x.kategorie) ?? '',
    name: x.name ?? '',
    min: x.minuten,
    via: [lab(x.verkehrsmittel), x.km != null ? `${num(x.km)}${NB}km` : null].filter(Boolean).join(' · '),
  }));

  const web = (k?.website ?? '').replace(/^https?:\/\//, '');
  const termin = (k?.termin ?? '').replace(/^https?:\/\//, '');
  const wa = (k?.whatsapp || k?.mobil || '').trim();
  const waDigits = wa.replace(/\D/g, '').replace(/^00/, '').replace(/^0/, '49');
  const waLink = `https://wa.me/${waDigits}?text=${encodeURIComponent(`Hallo, ich interessiere mich für das Objekt ${objnr}.`)}`;

  const imp = r.impressum ?? {};
  // Partnerzeile: Vertragspartner (rechtlicher Name) mit Marke, falls abweichend, und Anschrift.
  const partnerName = k
    ? k.vertragspartner && k.firma && k.vertragspartner !== k.firma
      ? `${k.vertragspartner} (${k.firma})`
      : k.vertragspartner || k.firma
    : '';
  const sameAddr = !!imp.anschrift && !!k?.anschrift && imp.anschrift === k.anschrift;
  const partnerZeile = imp.standort || (!sameAddr && partnerName && k?.anschrift ? `${partnerName}: ${k.anschrift}` : '');
  const impressum = [
    [imp.firma, imp.zusatz].filter(Boolean).join(' · '),
    imp.vertreten_durch ? `Geschäftsführung: ${imp.vertreten_durch}` : null,
    imp.anschrift,
    imp.register,
    imp.ust_id ? `USt-IdNr.: ${imp.ust_id}` : null,
    partnerZeile,
    imp.erlaubnis_34c || k?.erlaubnis,
    imp.aufsichtsbehoerde ? `Aufsichtsbehörde: ${imp.aufsichtsbehoerde}` : k?.aufsicht ? `Aufsichtsbehörde: ${k.aufsicht}` : null,
    imp.kontakt,
    r.datenschutz_url ? `Datenschutz: ${r.datenschutz_url.replace(/^https?:\/\//, '')}` : null,
  ].filter((x): x is string => has(x));

  const partnerBrand = k?.vertragspartner || k?.firma || imp.firma || 'Immobilien-Exposé';

  return {
    objnr,
    layout,
    brandMode,
    partnerBrand,
    status: opts.status,
    titel,
    titelSize,
    untertitel: o.untertitel ?? '',
    ortZeile,
    vermarktung: miete ? 'Miete' : 'Kauf',
    objekttyp: o.objekttyp || lab(o.objektart) || '',
    cover,
    facts: facts.slice(0, 3),
    kurz: o.kurzbeschreibung ?? '',
    highlights: o.highlights ?? [],
    objektdaten,
    preisLabel,
    preisWert,
    preise,
    provisionTitel,
    provisionText,
    img2,
    beschreibung: blocks(bes.objekt),
    ausstattung: blocks(bes.ausstattung),
    modernisierungen: (b.modernisierungen ?? []).map((m) => ({ jahr: m.jahr, text: m.beschreibung })),
    merkmale,
    merkmalZweispaltig: merkmale.length > 8,
    sideInfo,
    galerie,
    grundriss: hasGrundriss ? slot(plaene[0]!) : null,
    energie,
    energieAufLage: !hasGrundriss,
    lage: {
      nr: String(kapitel).padStart(2, '0'),
      titel: [a.ort, a.ortsteil].filter(Boolean).join(', '),
      text: bes.lage ?? '',
      entf,
      karte: karte ? slot(karte) : null,
      kartePlatzhalter: a.adresse_veroeffentlichen ? 'Lagekarte' : 'Lagekarte · Umkreis (Adresse wird nicht veröffentlicht)',
    },
    kontakt: {
      nr: String(kapitel + 1).padStart(2, '0'),
      headline: d.kontakt?.besichtigung_text || 'Besichtigungen nach Vereinbarung.',
      name: k?.name ?? '',
      position: k?.position ?? '',
      firma: k?.firma ?? '',
      anschrift: k?.anschrift ?? '',
      foto: k?.foto_url ?? '',
      zeilen: (
        [
          ['Telefon', k?.telefon],
          ['Mobil', k?.mobil],
          ['E-Mail', k?.email],
          ['Termin', termin],
          ['Web', web],
        ] as [string, string | undefined][]
      )
        .filter(([, v]) => has(v))
        .map(([key, v]) => ({ k: key, v: v! })),
    },
    whatsapp: { show: opts.whatsapp && !!waDigits, nummer: wa, link: waLink },
    sonstiges: bes.sonstiges ?? '',
    provisionshinweis: r.provisionshinweis || provisionText,
    gewaehr: r.angaben_ohne_gewaehr ?? '',
    impressum,
    pn,
  };
}

export type ExposeView = ReturnType<typeof buildExposeView>;
